// Mini Demos catalogue. Each entry corresponds to a real in-app guided tour
// (see `src/lib/onboarding/tours.ts`). No mock screens, no video files.
// Strictly presentational: tours never toggle real settings, never write
// preferences, never call permission APIs, never hit Supabase.

export type DemoAffects =
  | "Battery"
  | "Privacy"
  | "Uploads"
  | "Trip memories"
  | "Notifications"
  | "Sharing";

export type MiniDemo = {
  id: string;
  /** Tour id in `TOURS` — what Start tour will launch. */
  tourId: string;
  title: string;
  description: string;
  durationLabel: string;

  whyItMatters?: string;
  bestFor?: string;
  affects?: DemoAffects[];
};

export const MINI_DEMOS: MiniDemo[] = [
  {
    id: "tracking-settings",
    tourId: "tracking-settings",
    title: "Change tracking settings",
    description: "Walk through Settings to adjust how Roava records your route.",
    durationLabel: "1 min",
    whyItMatters:
      "Tracking settings affect how Roava records your route for maps and trip memories. Higher accuracy can improve detail, but may use more battery.",
    bestFor: "Choosing the right balance between route detail and battery life.",
    affects: ["Battery", "Trip memories"],
  },
  {
    id: "photo-access",
    tourId: "photo-access",
    title: "Manage photo access",
    description: "See where to choose which photos Roava can use for trip memories.",
    durationLabel: "1 min",
    whyItMatters:
      "Photo access lets Roava build trip memories from the pictures you choose. Limited access gives you more privacy control.",
    bestFor: "Deciding whether Roava can use all photos or only selected photos.",
    affects: ["Privacy", "Trip memories"],
  },
  {
    id: "upload-sync",
    tourId: "upload-sync",
    title: "Upload & sync settings",
    description: "Find where to control Wi-Fi, battery, and automatic upload behavior.",
    durationLabel: "1 min",
    whyItMatters:
      "Upload settings control when Roava sends photos and route data to the cloud, especially on Wi-Fi, low battery, or spotty service.",
    bestFor: "Managing data use and avoiding surprise battery drain.",
    affects: ["Battery", "Uploads"],
  },
  {
    id: "pod-grouping",
    tourId: "pod-grouping",
    title: "Pod grouping",
    description: "See where to change how nearby photos are grouped into stops.",
    durationLabel: "1 min",
    whyItMatters:
      "Pod grouping controls how nearby photos become stops in your trip timeline. Tighter grouping creates smaller stops; looser grouping combines more nearby moments.",
    bestFor: "Making trip memories feel organized without manually sorting every photo.",
    affects: ["Trip memories"],
  },
  {
    id: "family-follow",
    tourId: "family-follow",
    title: "Family Follow",
    description: "Find Family Follow on the Trip tab and see how delayed sharing works.",
    durationLabel: "1 min",
    whyItMatters:
      "Family Follow lets you share delayed trip updates through a private link without giving family traveler or admin access.",
    bestFor: "Keeping family updated while protecting exact location and personal trip details.",
    affects: ["Privacy", "Sharing"],
  },
  {
    id: "battery-offline",
    tourId: "battery-offline",
    title: "Battery & offline mode",
    description: "See how Roava handles low battery, Wi-Fi, and spotty service.",
    durationLabel: "1 min",
    whyItMatters:
      "Battery and offline settings help Roava keep working during long travel days without forcing every upload to happen immediately.",
    bestFor: "Long drives, remote areas, low signal, or conserving battery.",
    affects: ["Battery", "Uploads"],
  },
  {
    id: "notifications",
    tourId: "notifications",
    title: "Notifications",
    description: "See where trip alerts, sync reminders, and quiet hours live.",
    durationLabel: "1 min",
    whyItMatters:
      "Notification settings let you choose which Roava reminders matter, when they can appear, and how device or sync issues are surfaced.",
    bestFor: "Keeping useful alerts on while reducing noise.",
    affects: ["Notifications", "Uploads"],
  },
];
